import { ApiService } from './apiService';
import { triggerBlobDownload } from './userInvoiceService';
import { API_ENDPOINTS } from '../config/api';
import { PaginatedResponse } from '../types/api';
import { useAuthStore } from '../store/authStore';
import {
  Lead,
  CreateLeadRequest,
  UpdateLeadRequest,
  SearchLeadsRequest,
  LeadSourceOption,
  BulkExportLeadsRequest,
  BulkExportLeadsResponse,
  ImportLeadsResponse,
  BulkCreateTaskRequest,
  BulkCreateTaskResponse,
  TaskAssigneeOption,
  LeadStatus,
} from '../types/lead';

const MOCK_SOURCES: LeadSourceOption[] = [
  { id: 1, name: 'Organic' },
  { id: 2, name: 'Facebook' },
  { id: 3, name: 'Instagram' },
  { id: 4, name: 'Referral' },
  { id: 5, name: 'Walk-in' },
];

const MOCK_LEAD_DEFAULTS: Pick<
  Lead,
  'userId' | 'convertedUserId' | 'convertedAt' | 'taskAssignee'
> = {
  userId: null,
  convertedUserId: null,
  convertedAt: null,
  taskAssignee: null,
};

const MOCK_LEADS: Lead[] = [
  {
    ...MOCK_LEAD_DEFAULTS,
    leadId: '1',
    leadUniqueId: 's3w343',
    fullName: 'Ananya Das',
    email: 'ananya.das@email.com',
    phoneNumber: '+91 98450 22334',
    leadSource: MOCK_SOURCES[0],
    status: 'converted',
    gender: 'Female',
    preferredGender: 'Male',
    notes: null,
    convertedUserId: 101,
    convertedAt: '2025-05-12T00:00:00.000Z',
    createdByUserId: 2,
    createdAt: '2025-05-08T00:00:00.000Z',
    modifiedAt: '2025-05-12T00:00:00.000Z',
  },
  {
    ...MOCK_LEAD_DEFAULTS,
    leadId: '2',
    leadUniqueId: 'S4ULEAD102',
    fullName: 'Rahul Mehta',
    email: 'rahul.mehta@email.com',
    phoneNumber: '+91 98340 88990',
    leadSource: MOCK_SOURCES[1],
    status: 'progress',
    gender: 'Male',
    preferredGender: 'Female',
    notes: 'Interested in premium plan',
    leadDate: '2026-02-14',
    counsellorName: 'Ravi',
    age: '32',
    qualification: 'MBA',
    occupation: 'Engineer',
    caste: 'General',
    height: "5'10\"",
    maritalStatus: 'Single',
    location: 'Mumbai, Maharashtra, India',
    requirements: 'Professional match in same city',
    remarks1stTime: 'Called on 15/02',
    secondCallDate: 'Follow up scheduled',
    membership: 'Free trial started',
    createdByUserId: 2,
    createdAt: '2025-05-23T00:00:00.000Z',
    modifiedAt: '2025-05-24T00:00:00.000Z',
  },
  {
    ...MOCK_LEAD_DEFAULTS,
    leadId: '3',
    leadUniqueId: 's3w343',
    fullName: 'Sofia Vargas',
    email: 'sofia.vargas@email.com',
    phoneNumber: '+91 98760 99887',
    leadSource: MOCK_SOURCES[2],
    status: 'new_lead',
    gender: 'Female',
    preferredGender: 'Male',
    notes: null,
    convertedUserId: null,
    convertedAt: null,
    createdByUserId: 7,
    createdAt: '2025-05-24T00:00:00.000Z',
    modifiedAt: '2025-05-24T00:00:00.000Z',
  },
  {
    ...MOCK_LEAD_DEFAULTS,
    leadId: '4',
    leadUniqueId: 's3w343',
    fullName: 'Arjun Malhotra',
    email: 'arjun.m@email.com',
    phoneNumber: '+91 98201 33445',
    leadSource: MOCK_SOURCES[1],
    status: 'qualified',
    gender: 'Male',
    preferredGender: 'Female',
    notes: null,
    convertedUserId: null,
    convertedAt: null,
    createdByUserId: 2,
    createdAt: '2025-05-20T00:00:00.000Z',
    modifiedAt: '2025-05-22T00:00:00.000Z',
  },
  {
    ...MOCK_LEAD_DEFAULTS,
    leadId: '5',
    leadUniqueId: 's3w343',
    fullName: 'Neha Gupta',
    email: 'neha.gupta@email.com',
    phoneNumber: '+91 98102 77881',
    leadSource: MOCK_SOURCES[2],
    status: 'lead',
    gender: 'Female',
    preferredGender: 'Male',
    notes: null,
    convertedUserId: null,
    convertedAt: null,
    createdByUserId: 7,
    createdAt: '2025-05-18T00:00:00.000Z',
    modifiedAt: '2025-05-21T00:00:00.000Z',
  },
];

const mockLeadStore = [...MOCK_LEADS];

const blobFromBase64 = (contentBase64: string, contentType: string): Blob => {
  const binary = atob(contentBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: contentType });
};

export const normalizeLead = (lead: Lead): Lead => {
  const parts = lead.fullName.trim().split(/\s+/).filter(Boolean);
  return {
    ...lead,
    leadId: String(lead.leadId),
    firstName: parts[0] ?? '',
    lastName: parts.slice(1).join(' '),
    source: lead.leadSource?.name,
    uniqueId: lead.leadUniqueId,
    taskAssignee: lead.taskAssignee ?? null,
  };
};

const filterMockLeads = (leads: Lead[], params?: SearchLeadsRequest): Lead[] => {
  let result = leads.map(normalizeLead);

  if (params?.search?.trim()) {
    const q = params.search.trim().toLowerCase();
    result = result.filter(
      (l) =>
        l.fullName.toLowerCase().includes(q) ||
        l.email.toLowerCase().includes(q) ||
        (l.phoneNumber ?? '').toLowerCase().includes(q)
    );
  }

  if (params?.lead_type) {
    result = result.filter((l) => l.leadSource?.id === params.lead_type);
  }

  if (params?.status) {
    result = result.filter((l) => l.status === params.status);
  }

  if (params?.gender) {
    result = result.filter((l) => l.gender === params.gender);
  }

  return result;
};

export class LeadService extends ApiService {
  async getLeadSources(): Promise<LeadSourceOption[]> {
    return this.get<LeadSourceOption[]>(API_ENDPOINTS.leads.sources);
  }

  async createLeadSource(name: string): Promise<LeadSourceOption> {
    return this.post<LeadSourceOption>(API_ENDPOINTS.leads.createSource, { name });
  }

  async searchLeads(params?: SearchLeadsRequest): Promise<PaginatedResponse<Lead>> {
    const response = await this.post<PaginatedResponse<Lead>>(
      API_ENDPOINTS.leads.search,
      params ?? {}
    );
    return {
      ...response,
      data: response.data.map(normalizeLead),
    };
  }

  async getLeadById(id: string): Promise<Lead> {
    const lead = await this.get<Lead>(API_ENDPOINTS.leads.get(id));
    return normalizeLead(lead);
  }

  async createLead(data: CreateLeadRequest): Promise<Lead> {
    const authState = useAuthStore.getState();
    const adminUserId = authState.staffUserId ?? authState.user?.id;

    const lead = await this.post<Lead>(API_ENDPOINTS.leads.create, data, {
      headers: adminUserId != null ? { user_id: String(adminUserId) } : undefined,
    });
    return normalizeLead(lead);
  }

  async updateLead(id: string, data: UpdateLeadRequest): Promise<Lead> {
    const lead = await this.patch<Lead>(API_ENDPOINTS.leads.update(id), data);
    return normalizeLead(lead);
  }

  async deleteLead(id: string): Promise<void> {
    await this.delete<void>(API_ENDPOINTS.leads.delete(id));
  }

  async bulkDelete(leadIds: number[]): Promise<void> {
    await this.post<void>(API_ENDPOINTS.leads.bulkDelete, { leadIds });
  }

  async bulkExport(body: BulkExportLeadsRequest): Promise<BulkExportLeadsResponse> {
    return this.post<BulkExportLeadsResponse>(API_ENDPOINTS.leads.bulkExport, body);
  }

  async exportAndDownload(body: BulkExportLeadsRequest): Promise<void> {
    const format = body.format ?? 'xlsx';
    const response = await this.bulkExport({ ...body, format });

    let blob: Blob;
    if (response.contentBase64) {
      blob = blobFromBase64(response.contentBase64, response.contentType);
    } else if (response.csv) {
      blob = new Blob([response.csv], { type: response.contentType });
    } else {
      throw new Error('Export response missing file content');
    }

    triggerBlobDownload(blob, response.filename);
  }

  async getTaskAssignees(): Promise<TaskAssigneeOption[]> {
    const assignees = await this.get<TaskAssigneeOption[]>(API_ENDPOINTS.kanban.assignees);
    if (!Array.isArray(assignees)) {
      throw new Error('Unable to load task owners.');
    }
    return assignees;
  }

  async bulkCreateTask(
    leadIds: number[],
    title?: string,
    assigneeUserId?: number
  ): Promise<BulkCreateTaskResponse> {
    const body: BulkCreateTaskRequest = {
      leadIds,
      ...(title ? { title } : {}),
      ...(assigneeUserId != null && Number.isFinite(assigneeUserId)
        ? { assigneeUserId }
        : {}),
    };
    return this.post<BulkCreateTaskResponse>(API_ENDPOINTS.leads.bulkCreateTask, body);
  }

  async importLeads(file: File): Promise<ImportLeadsResponse> {
    const formData = new FormData();
    formData.append('file', file);
    return this.post<ImportLeadsResponse>(API_ENDPOINTS.leads.import, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }
}

export const leadService = new LeadService();

export interface LeadStats {
  total: number;
  newLead: number;
  progress: number;
  qualified: number;
  converted: number;
}

export const getLeadStats = (params?: SearchLeadsRequest): LeadStats => {
  const filtered = filterMockLeads(mockLeadStore, params);
  const countByStatus = (status: LeadStatus) =>
    filtered.filter((l) => l.status === status).length;

  return {
    total: filtered.length,
    newLead: countByStatus('new_lead') + countByStatus('lead'),
    progress: countByStatus('progress') + countByStatus('pending'),
    qualified: countByStatus('qualified'),
    converted: countByStatus('converted'),
  };
};
